import type { WallDefinition } from "./wallDefinitions";
import { OUTER_WALL_BALANCE_PROFILE } from "./wallBalanceProfiles";
import {
  DEFAULT_WALL_REWARD_CAP_LEVEL,
  type WallRewardCapLevel,
} from "./wallRewardCap";

export const OUTER_WALL_CLEAR_TARGET_HOV =
  OUTER_WALL_BALANCE_PROFILE.challenge.clearTargetHov;
export const OUTER_WALL_REWARD_TARGET_CLEAR_MS =
  OUTER_WALL_BALANCE_PROFILE.reward.targetClearMs;

export const OUTER_WALL_BUILT_IN_OHOV_AUTO_TARGET_MIN_TIER =
  OUTER_WALL_BALANCE_PROFILE.reward.minimumTier;
export const OUTER_WALL_BUILT_IN_OHOV_AUTO_TARGET_BASE_CAP_TIER =
  OUTER_WALL_BALANCE_PROFILE.reward.caps.base;
export const OUTER_WALL_BUILT_IN_OHOV_AUTO_TARGET_RELAXED_CAP_TIER =
  OUTER_WALL_BALANCE_PROFILE.reward.caps.relaxed;

const OUTER_WALL_BUILT_IN_OHOV_AUTO_TARGET_BASE_MANTISSA =
  OUTER_WALL_BALANCE_PROFILE.reward.targetBaseMantissa;
const OUTER_WALL_BUILT_IN_OHOV_AUTO_TARGET_EXPONENT_STEP =
  OUTER_WALL_BALANCE_PROFILE.reward.targetExponentStep;

export const OUTER_WALL_BUILT_IN_OHOV_AUTO_TARGET_INITIAL_HOV =
  formatOuterWallBuiltInOHovAutoTargetHov(
    OUTER_WALL_BUILT_IN_OHOV_AUTO_TARGET_MIN_TIER,
  );

export const OUTER_WALL_DEFINITION: WallDefinition = {
  id: "outer-wall",
  firstClearReward: OUTER_WALL_BALANCE_PROFILE.firstClearReward,
};

export interface OuterWallBuiltInOHovAutoTarget {
  readonly tier: number;
  readonly builtInOHovAutoTargetHov: string;
}

export function resolveOuterWallBuiltInOHovAutoTarget(
  bestClearMs: number | null,
  capLevel: WallRewardCapLevel = DEFAULT_WALL_REWARD_CAP_LEVEL,
): OuterWallBuiltInOHovAutoTarget {
  const tier = resolveOuterWallBuiltInOHovAutoTargetTier(
    bestClearMs,
    resolveOuterWallBuiltInOHovAutoTargetEffectiveMaxTier(capLevel),
  );
  return {
    tier,
    builtInOHovAutoTargetHov: formatOuterWallBuiltInOHovAutoTargetHov(tier),
  };
}

export function resolveOuterWallBuiltInOHovAutoTargetEffectiveMaxTier(
  capLevel: WallRewardCapLevel = DEFAULT_WALL_REWARD_CAP_LEVEL,
): number {
  return capLevel === "relaxed"
    ? OUTER_WALL_BUILT_IN_OHOV_AUTO_TARGET_RELAXED_CAP_TIER
    : OUTER_WALL_BUILT_IN_OHOV_AUTO_TARGET_BASE_CAP_TIER;
}

export function resolveOuterWallBuiltInOHovAutoTargetTier(
  bestClearMs: number | null,
  effectiveMaxTier: number = resolveOuterWallBuiltInOHovAutoTargetEffectiveMaxTier(),
): number {
  if (bestClearMs === null)
    return OUTER_WALL_BUILT_IN_OHOV_AUTO_TARGET_MIN_TIER;
  if (!Number.isFinite(bestClearMs) || bestClearMs <= 0) {
    return effectiveMaxTier;
  }

  const speedRatio = OUTER_WALL_REWARD_TARGET_CLEAR_MS / bestClearMs;
  const softcap = OUTER_WALL_BALANCE_PROFILE.reward.softcapSpeedRatio;
  const clearSpeedTier = Math.floor(
    speedRatio <= softcap
      ? speedRatio
      : softcap + Math.log2(speedRatio / softcap),
  );

  return Math.min(
    effectiveMaxTier,
    Math.max(OUTER_WALL_BUILT_IN_OHOV_AUTO_TARGET_MIN_TIER, clearSpeedTier + 1),
  );
}

function formatOuterWallBuiltInOHovAutoTargetHov(tier: number): string {
  return `${(OUTER_WALL_BUILT_IN_OHOV_AUTO_TARGET_BASE_MANTISSA * tier).toFixed(
    1,
  )}e${OUTER_WALL_BUILT_IN_OHOV_AUTO_TARGET_EXPONENT_STEP * tier}`;
}
